import React, { useState, useEffect, useContext, useCallback } from 'react';
import {
    Box,
    Typography,
    Button,
    Chip,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Grid,
    MenuItem,
    FormControlLabel,
    Switch,
    Alert,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Table,
    TableHead,
    TableBody,
    TableRow,
    TableCell,
    TableContainer,
    CircularProgress,
    Stack,
    Divider,
} from '@mui/material';
import {
    Add as AddIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    ExpandMore as ExpandMoreIcon,
    PictureAsPdf as PictureAsPdfIcon,
    UploadFile as UploadFileIcon,
    Language as LanguageIcon,
} from '@mui/icons-material';
import { TokenContext } from '../Context/TokenContext';
import { UserContext } from '../Context/UserContext';
import {
    SERVICE_PACKAGE_CATEGORIES,
    getServicePackages,
    createServicePackage,
    updateServicePackage,
    deleteServicePackage,
    createServicePackageOption,
    updateServicePackageOption,
    deleteServicePackageOption,
    uploadServicePackageBrochure,
    deleteServicePackageBrochure,
} from '../Utils/servicePackagesApi';
import {
    applyDiscount,
    buildDiscountLabel,
    buildExpirationLabel,
    formatEUR,
    openPrintSheet,
} from '../Utils/pdfExport';
import PdfExportDialog from '../Componant/PdfExport/PdfExportDialog';
import { buildProductSheetHtml } from '../Componant/PdfExport/productSheetTemplate';
import { getSettings } from '../Utils/settingsApi';

const API_URL = process.env.REACT_APP_BACKEND_API_URL || 'http://localhost:8080';

const emptyPackageForm = {
    slug: '', category: 'site', name: '', tagline: '', price_label: '',
    starting_price: '', features: '', promo_text: '', promo_ends_at: '',
    processing_fee_label: '', is_active: true, sort_order: 0,
};

const emptyOptionForm = { label: '', price_label: '', sort_order: 0 };

export default function ServicePackagesPage() {
    const { accessToken } = useContext(TokenContext);
    const { userInfo } = useContext(UserContext);
    const isSuperAdmin = userInfo?.is_super_admin === true;
    const userPermissions = userInfo?.permissions || [];
    const canManagePackages = isSuperAdmin || userPermissions.includes('service-packages.manage');
    const canDiscount = isSuperAdmin || userPermissions.includes('service-packages.pricing.discount');

    const [packages, setPackages] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [expanded, setExpanded] = useState(false);

    const [pkgDialogOpen, setPkgDialogOpen] = useState(false);
    const [editingPkg, setEditingPkg] = useState(null);
    const [pkgForm, setPkgForm] = useState(emptyPackageForm);
    const [pkgFormError, setPkgFormError] = useState('');

    const [optionDialogOpen, setOptionDialogOpen] = useState(false);
    const [editingOptionPkgId, setEditingOptionPkgId] = useState(null);
    const [editingOption, setEditingOption] = useState(null);
    const [optionForm, setOptionForm] = useState(emptyOptionForm);
    const [optionFormError, setOptionFormError] = useState('');

    const [pdfExportPkg, setPdfExportPkg] = useState(null);
    const [generatingPdf, setGeneratingPdf] = useState(false);
    const [defaultManagementFeeLabel, setDefaultManagementFeeLabel] = useState('');

    const fetchPackages = useCallback(() => {
        if (!accessToken) return;
        setLoading(true);
        getServicePackages(accessToken)
            .then((data) => setPackages([...data].sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0))))
            .catch(() => setError('Impossible de charger les prestations'))
            .finally(() => setLoading(false));
    }, [accessToken]);

    useEffect(() => { fetchPackages(); }, [fetchPackages]);

    useEffect(() => {
        if (!accessToken) return;
        getSettings(accessToken)
            .then((data) => setDefaultManagementFeeLabel(data.default_management_fee_label || ''))
            .catch(() => {});
    }, [accessToken]);

    // ── Dialog prestation ───────────────────────────────────────────────

    const openCreatePkg = () => {
        setEditingPkg(null);
        setPkgForm({ ...emptyPackageForm, processing_fee_label: defaultManagementFeeLabel });
        setPkgFormError('');
        setPkgDialogOpen(true);
    };

    const openEditPkg = (pkg) => {
        setEditingPkg(pkg);
        setPkgForm({
            slug: pkg.slug,
            category: pkg.category,
            name: pkg.name,
            tagline: pkg.tagline || '',
            price_label: pkg.price_label,
            starting_price: pkg.starting_price ?? '',
            features: (pkg.features || []).join('\n'),
            promo_text: pkg.promo_text || '',
            promo_ends_at: pkg.promo_ends_at ? pkg.promo_ends_at.slice(0, 10) : '',
            processing_fee_label: pkg.processing_fee_label || '',
            is_active: pkg.is_active !== false,
            sort_order: pkg.sort_order ?? 0,
        });
        setPkgFormError('');
        setPkgDialogOpen(true);
    };

    const handleSavePkg = () => {
        const payload = {
            ...pkgForm,
            starting_price: pkgForm.starting_price === '' ? null : Number(pkgForm.starting_price),
            features: pkgForm.features.split('\n').map((f) => f.trim()).filter(Boolean),
            promo_text: pkgForm.promo_text || null,
            promo_ends_at: pkgForm.promo_ends_at || null,
            processing_fee_label: pkgForm.processing_fee_label || null,
            sort_order: Number(pkgForm.sort_order) || 0,
        };

        const req = editingPkg
            ? updateServicePackage(accessToken, editingPkg.id, payload)
            : createServicePackage(accessToken, payload);

        req.then(() => { setPkgDialogOpen(false); fetchPackages(); })
           .catch((err) => setPkgFormError(err.response?.data?.message || "Erreur lors de l'enregistrement"));
    };

    const handleDeletePkg = (pkg) => {
        if (!window.confirm(`Supprimer la prestation "${pkg.name}" et ses options ?`)) return;
        deleteServicePackage(accessToken, pkg.id).then(fetchPackages).catch(() => setError('Suppression impossible'));
    };

    // ── Dialog option ───────────────────────────────────────────────────

    const openCreateOption = (pkgId) => {
        setEditingOptionPkgId(pkgId);
        setEditingOption(null);
        setOptionForm(emptyOptionForm);
        setOptionFormError('');
        setOptionDialogOpen(true);
    };

    const openEditOption = (pkgId, option) => {
        setEditingOptionPkgId(pkgId);
        setEditingOption(option);
        setOptionForm({ label: option.label, price_label: option.price_label, sort_order: option.sort_order ?? 0 });
        setOptionFormError('');
        setOptionDialogOpen(true);
    };

    const handleSaveOption = () => {
        const payload = { ...optionForm, sort_order: Number(optionForm.sort_order) || 0 };
        const req = editingOption
            ? updateServicePackageOption(accessToken, editingOptionPkgId, editingOption.id, payload)
            : createServicePackageOption(accessToken, editingOptionPkgId, payload);

        req.then(() => { setOptionDialogOpen(false); fetchPackages(); })
           .catch((err) => setOptionFormError(err.response?.data?.message || "Erreur lors de l'enregistrement"));
    };

    const handleDeleteOption = (pkgId, option) => {
        if (!window.confirm(`Supprimer l'option "${option.label}" ?`)) return;
        deleteServicePackageOption(accessToken, pkgId, option.id).then(fetchPackages).catch(() => setError('Suppression impossible'));
    };

    // ── Plaquette PDF ───────────────────────────────────────────────────

    const handleUploadBrochure = (pkgId, file) => {
        if (!file) return;
        uploadServicePackageBrochure(accessToken, pkgId, file).then(fetchPackages).catch(() => setError("Échec de l'upload de la plaquette"));
    };

    const handleDeleteBrochure = (pkgId) => {
        if (!window.confirm('Supprimer la plaquette PDF ?')) return;
        deleteServicePackageBrochure(accessToken, pkgId).then(fetchPackages).catch(() => setError('Suppression impossible'));
    };

    // ── Export PDF (fiche tarifaire, remise ponctuelle non persistée) ────

    const handleGeneratePdf = (discount, expirationDate, managementFeeLabel) => {
        const pkg = pdfExportPkg;
        if (!pkg) return;

        setGeneratingPdf(true);
        try {
            const basePrice = pkg.starting_price != null ? Number(pkg.starting_price) : null;
            const discounted = applyDiscount(basePrice, discount);
            const discountLabel = buildDiscountLabel(discount);

            const priceCaption = discountLabel ? `Offre spéciale ${discountLabel}` : 'Tarif de départ';
            const priceAmount = `${discounted != null ? formatEUR(discounted) : pkg.price_label} HT`;
            const originalPriceAmount = discounted != null
                ? `${basePrice != null ? formatEUR(basePrice) : pkg.price_label} HT`
                : null;

            const notes = [
                buildExpirationLabel(expirationDate),
                managementFeeLabel,
            ].filter(Boolean);

            const html = buildProductSheetHtml({
                documentTitle: `Tarification — ${pkg.name} — Alaïs-Studio`,
                productName: pkg.name,
                priceCaption,
                priceAmount,
                originalPriceAmount,
                sectionHeading: 'Ce qui est inclus',
                sectionSubtitle: pkg.tagline,
                features: pkg.features || [],
                options: (pkg.Options || []).map((o) => ({ label: o.label, priceLabel: o.price_label })),
                notes,
            });

            openPrintSheet(html);
            setPdfExportPkg(null);
        } catch {
            setError("Échec de la génération du PDF");
        } finally {
            setGeneratingPdf(false);
        }
    };

    return (
        <Box sx={{ p: { xs: 2, sm: 3 } }}>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <LanguageIcon sx={{ mr: 2, color: 'primary.main', fontSize: 32 }} />
                    <Typography variant="h4" sx={{ color: 'primary.main', fontWeight: 'bold' }}>
                        Prestations — sites & référencement
                    </Typography>
                </Box>
                {canManagePackages && (
                    <Button variant="contained" startIcon={<AddIcon />} onClick={openCreatePkg}
                        sx={{ bgcolor: 'primary.main', '&:hover': { bgcolor: '#a20000' } }}>
                        Nouvelle prestation
                    </Button>
                )}
            </Box>

            <Alert severity="info" sx={{ mb: 3 }}>
                Ces prestations alimentent la page publique "Nos prestations" du storefront
                (développement de sites sur-mesure et référencement, vendus sur devis) —
                indépendantes du catalogue d'applications SaaS.
            </Alert>

            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress size={28} /></Box>
            ) : packages.length === 0 ? (
                <Alert severity="info">Aucune prestation configurée pour le moment.</Alert>
            ) : (
                SERVICE_PACKAGE_CATEGORIES.map(({ value, label }) => {
                    const pkgsInCategory = packages.filter((p) => p.category === value);
                    if (pkgsInCategory.length === 0) return null;

                    return (
                        <Box key={value} sx={{ mb: 4 }}>
                            <Typography variant="h6" sx={{ mb: 1.5 }}>{label}</Typography>
                            {pkgsInCategory.map((pkg) => (
                                <Accordion
                                    key={pkg.id}
                                    expanded={expanded === pkg.id}
                                    onChange={(e, isExp) => setExpanded(isExp ? pkg.id : false)}
                                    sx={{ mb: 1 }}
                                >
                                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexGrow: 1, flexWrap: 'wrap' }}>
                                            <Typography sx={{ fontWeight: 600 }}>{pkg.name}</Typography>
                                            <Chip label={pkg.price_label} size="small" variant="outlined" />
                                            <Chip label={pkg.is_active ? 'Active' : 'Inactive'} size="small"
                                                color={pkg.is_active ? 'success' : 'default'} />
                                            {pkg.brochure_path && (
                                                <Chip icon={<PictureAsPdfIcon />} label="Plaquette PDF" size="small" color="info" variant="outlined" />
                                            )}
                                            <Box sx={{ flexGrow: 1 }} />
                                            {canDiscount && (
                                                <IconButton size="small" title="Exporter en PDF"
                                                    onClick={(e) => { e.stopPropagation(); setPdfExportPkg(pkg); }}>
                                                    <PictureAsPdfIcon fontSize="small" />
                                                </IconButton>
                                            )}
                                            {canManagePackages && (
                                                <>
                                                    <IconButton size="small" onClick={(e) => { e.stopPropagation(); openEditPkg(pkg); }}>
                                                        <EditIcon fontSize="small" />
                                                    </IconButton>
                                                    <IconButton size="small" color="error" onClick={(e) => { e.stopPropagation(); handleDeletePkg(pkg); }}>
                                                        <DeleteIcon fontSize="small" />
                                                    </IconButton>
                                                </>
                                            )}
                                        </Box>
                                    </AccordionSummary>
                                    <AccordionDetails>
                                        {pkg.tagline && (
                                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2, fontStyle: 'italic' }}>
                                                {pkg.tagline}
                                            </Typography>
                                        )}

                                        {pkg.features && pkg.features.length > 0 && (
                                            <Box sx={{ mb: 2 }}>
                                                {pkg.features.map((f, i) => (
                                                    <Chip key={i} label={f} size="small" sx={{ mr: 0.5, mb: 0.5 }} />
                                                ))}
                                            </Box>
                                        )}

                                        {(pkg.promo_text || pkg.processing_fee_label) && (
                                            <Stack spacing={0.5} sx={{ mb: 2 }}>
                                                {pkg.promo_text && (
                                                    <Typography variant="body2" sx={{ color: 'primary.main' }}>
                                                        Offre : {pkg.promo_text}
                                                        {pkg.promo_ends_at ? ` (jusqu'au ${pkg.promo_ends_at.slice(0, 10)})` : ''}
                                                    </Typography>
                                                )}
                                                {pkg.processing_fee_label && (
                                                    <Typography variant="body2" color="text.secondary">
                                                        {pkg.processing_fee_label}
                                                    </Typography>
                                                )}
                                            </Stack>
                                        )}

                                        <Divider sx={{ my: 2 }} />

                                        <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
                                            <Typography variant="subtitle2">Plaquette PDF</Typography>
                                            {pkg.brochure_path ? (
                                                <>
                                                    <Button size="small" href={`${API_URL}${pkg.brochure_path}`} target="_blank" rel="noopener noreferrer">
                                                        Voir le PDF
                                                    </Button>
                                                    {canManagePackages && (
                                                        <Button size="small" color="error" onClick={() => handleDeleteBrochure(pkg.id)}>
                                                            Supprimer
                                                        </Button>
                                                    )}
                                                </>
                                            ) : (
                                                <Typography variant="body2" color="text.secondary">Aucune</Typography>
                                            )}
                                            {canManagePackages && (
                                                <Button size="small" component="label" startIcon={<UploadFileIcon />}>
                                                    {pkg.brochure_path ? 'Remplacer' : 'Uploader'}
                                                    <input type="file" accept="application/pdf" hidden
                                                        onChange={(e) => handleUploadBrochure(pkg.id, e.target.files?.[0])} />
                                                </Button>
                                            )}
                                        </Stack>

                                        <Divider sx={{ my: 2 }} />

                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                            <Typography variant="subtitle2">Options tarifaires</Typography>
                                            {canManagePackages && (
                                                <Button size="small" startIcon={<AddIcon />} onClick={() => openCreateOption(pkg.id)}>
                                                    Ajouter une option
                                                </Button>
                                            )}
                                        </Box>

                                        {pkg.Options && pkg.Options.length > 0 ? (
                                            <TableContainer sx={{ overflowX: 'auto' }}>
                                            <Table size="small">
                                                <TableHead>
                                                    <TableRow>
                                                        <TableCell>Libellé</TableCell>
                                                        <TableCell>Prix</TableCell>
                                                        <TableCell align="right">Actions</TableCell>
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    {pkg.Options.map((option) => (
                                                        <TableRow key={option.id}>
                                                            <TableCell>{option.label}</TableCell>
                                                            <TableCell>{option.price_label}</TableCell>
                                                            <TableCell align="right">
                                                                {canManagePackages && (
                                                                    <>
                                                                        <IconButton size="small" onClick={() => openEditOption(pkg.id, option)}>
                                                                            <EditIcon fontSize="small" />
                                                                        </IconButton>
                                                                        <IconButton size="small" color="error" onClick={() => handleDeleteOption(pkg.id, option)}>
                                                                            <DeleteIcon fontSize="small" />
                                                                        </IconButton>
                                                                    </>
                                                                )}
                                                            </TableCell>
                                                        </TableRow>
                                                    ))}
                                                </TableBody>
                                            </Table>
                                            </TableContainer>
                                        ) : (
                                            <Typography variant="body2" color="text.secondary">Aucune option configurée.</Typography>
                                        )}
                                    </AccordionDetails>
                                </Accordion>
                            ))}
                        </Box>
                    );
                })
            )}

            {/* Dialog prestation */}
            <Dialog open={pkgDialogOpen} onClose={() => setPkgDialogOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>{editingPkg ? 'Modifier la prestation' : 'Nouvelle prestation'}</DialogTitle>
                <DialogContent>
                    <Grid container spacing={2} sx={{ mt: 0.5 }}>
                        {pkgFormError && <Grid item xs={12}><Alert severity="error">{pkgFormError}</Alert></Grid>}
                        <Grid item xs={12} sm={6}>
                            <TextField fullWidth size="small" label="Slug (ex: site-vitrine)"
                                value={pkgForm.slug} onChange={(e) => setPkgForm((f) => ({ ...f, slug: e.target.value }))} />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField fullWidth size="small" select label="Catégorie"
                                value={pkgForm.category} onChange={(e) => setPkgForm((f) => ({ ...f, category: e.target.value }))}>
                                {SERVICE_PACKAGE_CATEGORIES.map((c) => (
                                    <MenuItem key={c.value} value={c.value}>{c.label}</MenuItem>
                                ))}
                            </TextField>
                        </Grid>
                        <Grid item xs={12}>
                            <TextField fullWidth size="small" label="Nom (ex: Site vitrine)"
                                value={pkgForm.name} onChange={(e) => setPkgForm((f) => ({ ...f, name: e.target.value }))} />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField fullWidth size="small" label="Accroche (ex: Un site vitrine à votre image...)"
                                value={pkgForm.tagline} onChange={(e) => setPkgForm((f) => ({ ...f, tagline: e.target.value }))} />
                        </Grid>
                        <Grid item xs={12} sm={7}>
                            <TextField fullWidth size="small" label="Prix affiché (ex: À partir de 1 500 €)"
                                value={pkgForm.price_label} onChange={(e) => setPkgForm((f) => ({ ...f, price_label: e.target.value }))} />
                        </Grid>
                        <Grid item xs={12} sm={5}>
                            <TextField fullWidth size="small" type="number" label="Prix (tri uniquement)"
                                value={pkgForm.starting_price} onChange={(e) => setPkgForm((f) => ({ ...f, starting_price: e.target.value }))} />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField fullWidth size="small" multiline rows={5}
                                label="Fonctionnalités incluses (une par ligne)"
                                value={pkgForm.features} onChange={(e) => setPkgForm((f) => ({ ...f, features: e.target.value }))} />
                        </Grid>
                        <Grid item xs={12} sm={7}>
                            <TextField fullWidth size="small" label="Texte offre spéciale (optionnel)"
                                value={pkgForm.promo_text} onChange={(e) => setPkgForm((f) => ({ ...f, promo_text: e.target.value }))} />
                        </Grid>
                        <Grid item xs={12} sm={5}>
                            <TextField fullWidth size="small" type="date" label="Fin de l'offre" InputLabelProps={{ shrink: true }}
                                value={pkgForm.promo_ends_at} onChange={(e) => setPkgForm((f) => ({ ...f, promo_ends_at: e.target.value }))} />
                        </Grid>
                        <Grid item xs={12} sm={8}>
                            <TextField fullWidth size="small" label="Frais de dossier (ex: Frais de dossier 15 %)"
                                value={pkgForm.processing_fee_label} onChange={(e) => setPkgForm((f) => ({ ...f, processing_fee_label: e.target.value }))} />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <TextField fullWidth size="small" type="number" label="Ordre d'affichage"
                                value={pkgForm.sort_order} onChange={(e) => setPkgForm((f) => ({ ...f, sort_order: e.target.value }))} />
                        </Grid>
                        <Grid item xs={12}>
                            <FormControlLabel
                                control={<Switch checked={pkgForm.is_active}
                                    onChange={(e) => setPkgForm((f) => ({ ...f, is_active: e.target.checked }))} />}
                                label="Prestation active (visible sur le site)"
                            />
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setPkgDialogOpen(false)}>Annuler</Button>
                    <Button variant="contained" onClick={handleSavePkg}
                        disabled={!pkgForm.slug || !pkgForm.name || !pkgForm.price_label}
                        sx={{ bgcolor: 'primary.main', '&:hover': { bgcolor: '#a20000' } }}>
                        {editingPkg ? 'Modifier' : 'Créer'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Dialog option */}
            <Dialog open={optionDialogOpen} onClose={() => setOptionDialogOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>{editingOption ? "Modifier l'option" : 'Nouvelle option'}</DialogTitle>
                <DialogContent>
                    <Grid container spacing={2} sx={{ mt: 0.5 }}>
                        {optionFormError && <Grid item xs={12}><Alert severity="error">{optionFormError}</Alert></Grid>}
                        <Grid item xs={12}>
                            <TextField fullWidth size="small" label="Libellé (ex: Page(s) supplémentaire(s))"
                                value={optionForm.label} onChange={(e) => setOptionForm((f) => ({ ...f, label: e.target.value }))} />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField fullWidth size="small" label="Prix affiché (ex: 200 € / page)"
                                value={optionForm.price_label} onChange={(e) => setOptionForm((f) => ({ ...f, price_label: e.target.value }))} />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField fullWidth size="small" type="number" label="Ordre d'affichage"
                                value={optionForm.sort_order} onChange={(e) => setOptionForm((f) => ({ ...f, sort_order: e.target.value }))} />
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOptionDialogOpen(false)}>Annuler</Button>
                    <Button variant="contained" onClick={handleSaveOption}
                        disabled={!optionForm.label || !optionForm.price_label}
                        sx={{ bgcolor: 'primary.main', '&:hover': { bgcolor: '#a20000' } }}>
                        {editingOption ? 'Modifier' : 'Créer'}
                    </Button>
                </DialogActions>
            </Dialog>

            <PdfExportDialog
                open={Boolean(pdfExportPkg)}
                onClose={() => setPdfExportPkg(null)}
                onGenerate={handleGeneratePdf}
                generating={generatingPdf}
                defaultManagementFeeLabel={pdfExportPkg?.processing_fee_label || defaultManagementFeeLabel}
            />
        </Box>
    );
}
